import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="salesforce"
export default class extends Controller {
  static targets = ["connectionStatus"]

  connect() {
    console.log("Salesforce controller connected")
  }

  async testConnection(event) {
    event.preventDefault()
    const button = event.target
    const originalText = button.textContent
    
    // Disable button and show loading state
    button.disabled = true
    button.textContent = "Testing..."
    button.classList.add("opacity-50")

    try {
      const response = await fetch("/admin/salesforce/test_connection", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification("Successfully connected to Salesforce", "success")
        this.updateConnectionStatus(true)
      } else {
        this.showNotification(`Connection failed: ${result.error}`, "error")
        this.updateConnectionStatus(false)
      }
    } catch (error) {
      console.error("Test connection error:", error)
      this.showNotification("Failed to test connection", "error")
      this.updateConnectionStatus(false)
    } finally {
      // Restore button state
      button.disabled = false
      button.textContent = originalText
      button.classList.remove("opacity-50")
    }
  }

  async bulkSync(event) {
    event.preventDefault()
    const button = event.target
    const originalText = button.textContent

    if (!confirm("This will sync all eligible applications to Salesforce. Continue?")) {
      return
    }

    // Disable button and show loading state
    button.disabled = true
    button.textContent = "Syncing..."
    button.classList.add("opacity-50")

    try {
      const response = await fetch("/admin/salesforce/bulk_sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification(`${result.message}`, "success")
        // Reload page to show updated stats
        setTimeout(() => window.location.reload(), 2000)
      } else {
        this.showNotification(`Bulk sync failed: ${result.message}`, "error")
      }
    } catch (error) {
      console.error("Bulk sync error:", error)
      this.showNotification("Failed to start bulk sync", "error")
    } finally {
      // Restore button state
      button.disabled = false
      button.textContent = originalText
      button.classList.remove("opacity-50")
    }
  }

  async retryFailed(event) {
    event.preventDefault()
    const button = event.target
    const originalText = button.textContent

    if (!confirm("This will retry all failed Salesforce syncs. Continue?")) {
      return
    }

    // Disable button and show loading state
    button.disabled = true
    button.textContent = "Retrying..."
    button.classList.add("opacity-50")

    try {
      const response = await fetch("/admin/salesforce/retry_failed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification(`${result.message}`, "success")
        // Reload page to show updated stats
        setTimeout(() => window.location.reload(), 2000)
      } else {
        this.showNotification(`Retry failed: ${result.message}`, "error")
      }
    } catch (error) {
      console.error("Retry failed error:", error)
      this.showNotification("Failed to retry failed syncs", "error")
    } finally {
      // Restore button state
      button.disabled = false
      button.textContent = originalText
      button.classList.remove("opacity-50")
    }
  }

  async syncApplication(event) {
    event.preventDefault()
    const button = event.target
    const applicationId = button.dataset.applicationId
    const originalText = button.textContent

    if (!applicationId) {
      this.showNotification("Application ID not found", "error")
      return
    }

    // Disable button and show loading state
    button.disabled = true
    button.textContent = "Syncing..."
    button.classList.add("opacity-50")

    try {
      const response = await fetch(`/admin/salesforce/loan_applications/${applicationId}/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification("Application synced successfully", "success")
        // Remove the failed sync item from the list
        const listItem = button.closest("li")
        if (listItem) {
          listItem.remove()
        }
      } else {
        this.showNotification(`Sync failed: ${result.message}`, "error")
      }
    } catch (error) {
      console.error("Sync application error:", error)
      this.showNotification("Failed to sync application", "error")
    } finally {
      // Restore button state
      button.disabled = false
      button.textContent = originalText
      button.classList.remove("opacity-50")
    }
  }

  updateConnectionStatus(connected) {
    if (this.hasConnectionStatusTarget) {
      const statusElement = this.connectionStatusTarget
      const statusSpan = statusElement.querySelector("span")
      
      if (connected) {
        statusSpan.className = "inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
        statusSpan.innerHTML = `
          <svg class="-ml-0.5 mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
          Connected
        `
      } else {
        statusSpan.className = "inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
        statusSpan.innerHTML = `
          <svg class="-ml-0.5 mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
          Disconnected
        `
      }
    }
  }

  showNotification(message, type = "info") {
    // Create a notification element
    const notification = document.createElement("div")
    notification.className = `fixed top-4 right-4 z-50 max-w-sm w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden ${this.getNotificationClasses(type)}`
    
    notification.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            ${this.getNotificationIcon(type)}
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium ${this.getNotificationTextColor(type)}">${message}</p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button class="inline-flex ${this.getNotificationButtonColor(type)} rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2" onclick="this.parentElement.parentElement.parentElement.parentElement.remove()">
              <span class="sr-only">Close</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    // Add to page
    document.body.appendChild(notification)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove()
      }
    }, 5000)
  }

  getNotificationClasses(type) {
    switch (type) {
      case "success":
        return "bg-green-50 border border-green-200"
      case "error":
        return "bg-red-50 border border-red-200"
      case "warning":
        return "bg-yellow-50 border border-yellow-200"
      default:
        return "bg-blue-50 border border-blue-200"
    }
  }

  getNotificationTextColor(type) {
    switch (type) {
      case "success":
        return "text-green-800"
      case "error":
        return "text-red-800"
      case "warning":
        return "text-yellow-800"
      default:
        return "text-blue-800"
    }
  }

  getNotificationButtonColor(type) {
    switch (type) {
      case "success":
        return "text-green-400 hover:text-green-500"
      case "error":
        return "text-red-400 hover:text-red-500"
      case "warning":
        return "text-yellow-400 hover:text-yellow-500"
      default:
        return "text-blue-400 hover:text-blue-500"
    }
  }

  getNotificationIcon(type) {
    switch (type) {
      case "success":
        return `<svg class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`
      case "error":
        return `<svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>`
      case "warning":
        return `<svg class="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>`
      default:
        return `<svg class="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l3.5-1.75a.75.75 0 000-1.342l-3.5-1.75a.75.75 0 00-1.063.853l.708 2.836z" />
        </svg>`
    }
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}